import { rulesFor } from '@full-elevator/content';
import type { BuildingDef, LevelDef, PassengerTraits } from '@full-elevator/sim';
import { crossesBoundary, traitsFor } from '@full-elevator/sim';
import { debuts } from '../meet.js';
import type { SaveData } from '../storage.js';
import { isUnlocked } from '../storage.js';

/**
 * What the home screen shows (ADR-0019), worked out without touching the DOM.
 *
 * The home screen is the building the player is in: one floor per shift, the
 * lift parked at the next one. This decides which building that is, what state
 * each floor is in, and who is waiting on the next floor — so the rules about
 * what the screen *says* can be tested apart from how it is drawn.
 */
export type FloorState =
  /** Won at least once: lit, with its stars. */
  | 'played'
  /** The next shift on the roster: lit, the lift waiting, people on the landing. */
  | 'next'
  /** Open but not won — a shift lost, or every level unlocked for testing. */
  | 'open'
  /** Not reached yet: the room is there, the lights are not. */
  | 'locked';

/** A furnished room, by building. Picked by position in the building, never per level. */
export type RoomKind =
  | 'office-lobby'
  | 'office-desks'
  | 'office-meeting'
  | 'office-cafeteria'
  | 'office-executive'
  | 'hotel-reception'
  | 'hotel-corridor'
  | 'hotel-luggage'
  | 'hotel-housekeeping'
  | 'hotel-suite'
  | 'hotel-restaurant'
  | 'transit-platform'
  | 'transit-concourse'
  | 'transit-tickets'
  | 'skyline-skylobby'
  | 'skyline-office'
  | 'skyline-view';

const ROOMS: Readonly<Record<string, readonly RoomKind[]>> = {
  // The first shift of the game is in the lobby; the rest of the tower cycles.
  'office-tower': [
    'office-lobby',
    'office-desks',
    'office-meeting',
    'office-desks',
    'office-cafeteria',
    'office-desks',
    'office-meeting',
    'office-executive',
  ],
  // Rugs first: three of them, the hotel's own rule.
  'grand-hotel': [
    'hotel-corridor',
    'hotel-luggage',
    'hotel-housekeeping',
    'hotel-suite',
    'hotel-restaurant',
  ],
  // The offices over the station; the station itself is under the street (BELOW).
  'transit-centre': ['office-desks', 'office-meeting'],
  // The sky lobby first: where everyone changes lifts.
  'skyline-tower': ['skyline-skylobby', 'skyline-office', 'skyline-view', 'skyline-office'],
};

/**
 * Under the street, from the pavement down (ADR-0028): the hall the trains
 * empty into at −1, the metro platform under it at −2.
 */
const BELOW: Readonly<Record<string, readonly RoomKind[]>> = {
  'transit-centre': ['transit-concourse', 'transit-platform'],
};

/** The room a shift's floor shows: by depth under the street, or by its place above it. */
export function roomFor(
  buildingId: string,
  position: number,
  depth: number | null = null,
): RoomKind {
  const below = BELOW[buildingId];
  if (depth !== null && below) return below[Math.min(-depth, below.length) - 1] as RoomKind;
  const cycle = ROOMS[buildingId] ?? ROOMS['office-tower'] ?? ['office-desks'];
  if (buildingId === 'office-tower' && position > 0) {
    // After the lobby, never the lobby again.
    const rest = cycle.slice(1);
    return rest[(position - 1) % rest.length] as RoomKind;
  }
  return cycle[position % cycle.length] as RoomKind;
}

/** Someone waiting for the next shift, as the game would draw them. */
export interface Preview {
  readonly id: string;
  readonly destination: number;
  /**
   * Where they get out next. Their destination, except across an interchange,
   * where the ticket says the transfer floor first — `6›11` — as it does in play.
   */
  readonly nextStop: number;
  readonly traits: PassengerTraits;
}

export interface HomeFloor {
  readonly level: LevelDef;
  /** Position in play order, from 0. */
  readonly index: number;
  readonly stars: number;
  /** Its number in this building, from 1: what the plaque says (not `index`, which is play order). */
  readonly number: number;
  /**
   * Below the street (ADR-0028), as many storeys down as the building's own
   * ground floor is up: the Transit Centre's first two shifts are its metro.
   * Null above ground.
   */
  readonly depth: number | null;
  readonly state: FloorState;
  readonly room: RoomKind;
  /** The shift before this one, for a locked floor to say what opens it. */
  readonly after: string | null;
  /**
   * Someone the player has not met yet, who first appears on this shift
   * (ADR-0037): drawn as a silhouette until they are met, so there is always
   * a next face to play towards.
   */
  readonly newFace: string | null;
  /**
   * The shift's bonus goal (ADR-0040): met at least once, or still to meet.
   * Null where the shift has none.
   */
  readonly medal: 'met' | 'open' | null;
}

export interface HomeBuilding {
  readonly def: BuildingDef;
  /** Bottom to top, which is also play order. */
  readonly floors: readonly HomeFloor[];
  readonly reached: boolean;
  readonly stars: number;
}

export interface HomeModel {
  readonly buildings: readonly HomeBuilding[];
  /** The building the screen opens on: where the next shift is, or the last one reached. */
  readonly current: number;
  readonly next: HomeFloor | null;
  /** Nothing played yet: the screen shows the title and one button, nothing else. */
  readonly fresh: boolean;
  readonly totalStars: number;
  readonly played: number;
}

export function homeModel(
  data: SaveData,
  levels: readonly LevelDef[],
  buildings: readonly BuildingDef[],
): HomeModel {
  // The frontier, as the operations board defined it: the first level with no record.
  const nextIndex = levels.findIndex((l) => !data.records[l.id]);
  const homes = buildings.map((def) => buildingOf(def, data, levels, nextIndex));

  const next = homes.flatMap((b) => b.floors).find((f) => f.state === 'next') ?? null;
  const withNext = homes.findIndex((b) => b.floors.some((f) => f.state === 'next'));
  const lastReached = homes.reduce((last, b, i) => (b.reached ? i : last), 0);
  const played = Object.values(data.records).length;

  return {
    buildings: homes,
    current: withNext >= 0 ? withNext : lastReached,
    next,
    fresh: played === 0 && data.coins === 0,
    totalStars: homes.reduce((sum, b) => sum + b.stars, 0),
    played,
  };
}

/** Someone a shift brings first, if the player has not met them yet (ADR-0037). */
function unmet(debut: string | undefined, data: SaveData): string | null {
  return debut !== undefined && !data.seenArchetypes.includes(debut) ? debut : null;
}

/**
 * How far under the street a building's shift at `position` is: null above
 * ground (ADR-0028). A basement is entered from the top, so the first shift is
 * at −1 and the next one under it (PLAYTEST_LOG, 2026-10-20).
 */
function depthOf(def: BuildingDef, position: number): number | null {
  const ground = def.groundFloor ?? 0;
  return position < ground ? -(position + 1) : null;
}

/** Above ground, which of the building's floors this is, counting from the street. */
function aboveOf(def: BuildingDef, position: number): number {
  return position - (def.groundFloor ?? 0);
}

/** The shift's bonus medal (ADR-0040): met, still to meet, or none on this shift. */
function medalOf(level: LevelDef, data: SaveData): HomeFloor['medal'] {
  if (!level.bonus) return null;
  return data.records[level.id]?.bonus ? 'met' : 'open';
}

function buildingOf(
  def: BuildingDef,
  data: SaveData,
  levels: readonly LevelDef[],
  nextIndex: number,
): HomeBuilding {
  const ids = levels.map((l) => l.id);
  const firsts = debuts(levels);
  const floors: HomeFloor[] = [];
  levels.forEach((level, index) => {
    if (level.buildingId !== def.id) return;
    const stars = data.records[level.id]?.stars ?? 0;
    const unlocked = isUnlocked(data, index, ids);
    const state: FloorState =
      index === nextIndex ? 'next' : stars > 0 ? 'played' : unlocked ? 'open' : 'locked';
    floors.push({
      level,
      index,
      stars,
      number: floors.length + 1,
      depth: depthOf(def, floors.length),
      state,
      room: roomFor(def.id, aboveOf(def, floors.length), depthOf(def, floors.length)),
      after: index > 0 ? (levels[index - 1]?.name ?? null) : null,
      newFace: unmet(firsts.get(level.id), data),
      medal: medalOf(level, data),
    });
  });
  return {
    def,
    floors,
    reached: floors.some((f) => f.state !== 'locked'),
    stars: floors.reduce((sum, f) => sum + f.stars, 0),
  };
}

/**
 * Up to three people from the shift itself, waiting on its landing.
 *
 * The preview must not promise what the shift does not send (ADR-0019), so it
 * is drawn from the level's own arrivals: one of each kind it has first — a
 * VIP, a courier, a party — then the earliest of the rest.
 */
export function previewFor(level: LevelDef, count = 3): Preview[] {
  // Nobody who only appears later: a call-out's return is upstairs, not here (ADR-0029).
  const spawns = level.spawns
    .filter((s) => s.follows === undefined)
    .sort((a, b) => a.atMs - b.atMs);
  const kind = (s: (typeof spawns)[number]): string =>
    s.group ? 'group' : (s.archetype ?? 'normal');
  const picked: typeof spawns = [];
  const seen = new Set<string>();
  for (const spawn of spawns) {
    if (picked.length >= count) break;
    if (kind(spawn) === 'normal' || seen.has(kind(spawn))) continue;
    seen.add(kind(spawn));
    picked.push(spawn);
  }
  for (const spawn of spawns) {
    if (picked.length >= count) break;
    if (!picked.includes(spawn)) picked.push(spawn);
  }
  const rules = rulesFor(level.buildingId);
  return picked.map((spawn, i) => ({
    id: `${level.id}-${i}`,
    destination: spawn.destination,
    nextStop:
      rules.interchange && crossesBoundary(rules, spawn.origin, spawn.destination)
        ? rules.interchange.floor
        : spawn.destination,
    traits: traitsFor(spawn),
  }));
}
